import { Icon } from '@/styles/Icon';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

export interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean; // 위험한 액션 (빨간색 표시)
}

interface KebabMenuProps {
  menuItems: MenuItem[];
  className?: string;
}

export const KebabMenu: React.FC<KebabMenuProps> = ({
  menuItems,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMenuItemClick = (e: React.MouseEvent, item: MenuItem) => {
    e.stopPropagation();
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 부모 요소의 클릭 이벤트 전파 방지
    setIsOpen(!isOpen);
  };

  return (
    <MenuContainer ref={menuRef} className={className}>
      <KebabButton
        onClick={handleButtonClick}
        aria-label="메뉴 열기"
      >
        <Icon name="kebabWhite" size={24} />
      </KebabButton>

      {isOpen && (
        <MenuDropdown>
          {menuItems.map((item, index) => (
            <MenuItemButton
              key={index}
              onClick={(e) => handleMenuItemClick(e, item)}
              disabled={item.disabled}
              $danger={item.danger}
            >
              {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
              <MenuItemLabel>{item.label}</MenuItemLabel>
            </MenuItemButton>
          ))}
        </MenuDropdown>
      )}
    </MenuContainer>
  );
};

// Styled Components
const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const KebabButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: -14px;
  min-width: 180px;
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderGray};
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  z-index: 1000;
  overflow: hidden;
`;

const MenuItemButton = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.errorRed || '#dc2626' : theme.colors.fontBlack};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.bgGray[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.bgGray[200]};
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const MenuItemLabel = styled.span`
  flex: 1;
  white-space: nowrap;
`;
